import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

export type DialogContextType = {
  isDialogOpen: boolean;
  showDialog: (content: any) => void;
  hideDialog: () => void;
  showConfirmDialog: (content: any) => void;
  hideConfirmDialog: () => void;
  openDrawer: ({ content }: { content: React.ReactNode }) => void;
  closeDrawer: () => void;
  dialogContent: {
    content: ReactNode;
  };
  drawerContent: {
    opened: boolean;
    content: ReactNode;
  };
  isConfirmDialogOpen: boolean;
  confirmDialogContent?: {
    title?: string | null;
    description?: string | null;
    shouldAutoClose?: boolean;
    buttons?: {
      title?: string;
      onClick?: () => void;
    }[];
  } | null;
};

const defaultDialogConfig = {
  content: null,
};

const defaultDrawerConfig = {
  opened: false,
  content: null,
};

const defaultConfirmDialogConfig = {
  title: null,
  description: null,
  shouldAutoClose: false,
  buttons: [],
};

export const DialogContext = createContext<DialogContextType>({
  isDialogOpen: false,
  showDialog: (content: any) => {},
  hideDialog: () => {},
  showConfirmDialog: (content: any) => {},
  hideConfirmDialog: () => {},
  openDrawer: ({ content }: { content: React.ReactNode }) => {},
  closeDrawer: () => {},
  drawerContent: defaultDrawerConfig,
  dialogContent: defaultDialogConfig,
  isConfirmDialogOpen: false,
  confirmDialogContent: null,
});

export const DialogProvider = ({ children }: PropsWithChildren<{}>) => {
  const [dialogContent, setDialogContent] = useState(defaultDialogConfig);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Confirm dialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmDialogContent, setConfirmDialogContent] = useState(defaultConfirmDialogConfig);
  // Drawer
  const [drawerContent, setDrawerContent] = useState(defaultDrawerConfig);

  const openDrawer = (config: any): void => {
    setDrawerContent({ ...config, opened: true });
  };

  const closeDrawer = () => {
    setDrawerContent(defaultDrawerConfig);
  };

  // Confirm dialog
  const showConfirmDialog = (config: any): void => {
    setConfirmDialogContent(config);
    setIsConfirmDialogOpen(true);
  };

  const hideConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setConfirmDialogContent(defaultConfirmDialogConfig);
  };

  // Dialog
  const showDialog = (config: any): void => {
    setDialogContent(config);
    setIsDialogOpen(true);
  };

  const hideDialog = () => {
    setIsDialogOpen(false);
    setDialogContent(defaultDialogConfig);
  };

  return (
    <DialogContext.Provider
      value={{
        isDialogOpen,
        showDialog,
        hideDialog,
        dialogContent,
        isConfirmDialogOpen,
        confirmDialogContent,
        showConfirmDialog,
        hideConfirmDialog,
        openDrawer,
        drawerContent,
        closeDrawer,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export function useDialog() {
  return useContext(DialogContext);
}
